import { useEffect, useState } from 'react';
import {
  Button,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { Check, X } from 'lucide-react';
import {
  useServiceNotificationMailers,
  type ServiceDetail,
  type TicketWorkflowStep,
} from '@/api/services';
import { useCompleteWorkflowStep, useRejectWorkflowStep } from '@/api/services-mutations';
import { MAILER_STEP_NAME, stepDescription } from './workflow-step-labels';

function errMsg(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong.';
}

const muted = { color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' } as const;

/**
 * Inline approve/reject form for the currently-active (`doing`) workflow step.
 * The `cskh_verification` step needs a customer mailer picked (its details go
 * into `filled_data`) and is gated on the service having a sale-tracking owner
 * (backend rule); other CS steps just approve or reject with a reason.
 */
export function WorkflowStepForm({ detail, step }: { detail: ServiceDetail; step: TicketWorkflowStep }) {
  const id = detail.service_id;
  const toast = useToast();
  const complete = useCompleteWorkflowStep(id);
  const reject = useRejectWorkflowStep(id);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [mailerId, setMailerId] = useState('');

  const needsMailer = step.step_name === MAILER_STEP_NAME;
  const mailers = useServiceNotificationMailers(id, needsMailer);
  const rows = mailers.data?.results ?? [];
  const selected = rows.find((m) => String(m.customer_mailer_id) === mailerId);

  useEffect(() => { setRejecting(false); setReason(''); setMailerId(''); }, [step.step_id]);

  const pending = complete.isPending || reject.isPending;

  const approve = () => {
    // Sale-tracking owner is required before the CS-verification step can pass.
    if (needsMailer && (detail.employee_sale_tracking_ids?.length ?? 0) === 0) {
      toast.error('Assign a sale tracking owner before verifying.');
      return;
    }
    if (needsMailer && !mailerId) { toast.error('Please choose a mailer.'); return; }

    const filledData = needsMailer && selected
      ? {
          customer_mailer_id: selected.customer_mailer_id,
          mailer_receiver: selected.mailer_receiver ?? '',
          mailer_cc: selected.mailer_cc ?? '',
          mailer_bcc: selected.mailer_bcc ?? '',
        }
      : undefined;

    complete.mutate(
      { stepId: step.step_id, filledData },
      {
        onSuccess: () => toast.success('Step approved'),
        onError: (e) => toast.error(errMsg(e)),
      },
    );
  };

  const submitReject = () => {
    if (!reason.trim()) { toast.error('Please enter a reason.'); return; }
    reject.mutate(
      { stepId: step.step_id, reason: reason.trim() },
      {
        onSuccess: () => { toast.success('Step rejected'); setRejecting(false); setReason(''); },
        onError: (e) => toast.error(errMsg(e)),
      },
    );
  };

  const description = stepDescription(step.step_name);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      {description ? <span style={muted}>{description}</span> : null}

      {needsMailer ? (
        <>
          <Field label="Customer mailer" required>
            {() => (
              <Select value={mailerId} onValueChange={setMailerId} disabled={mailers.isLoading}>
                <SelectTrigger><SelectValue placeholder={mailers.isLoading ? 'Loading mailers…' : 'Choose a mailer…'} /></SelectTrigger>
                <SelectContent>
                  {rows.map((m) => (
                    <SelectItem key={m.customer_mailer_id} value={String(m.customer_mailer_id)}>
                      {m.customer_mailer_name}{m.mailer_receiver ? ` (${m.mailer_receiver})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          {selected ? (
            <div style={{ ...muted, display: 'grid', gap: 'var(--vn-space-1)', background: 'var(--vn-bg-muted)', padding: 'var(--vn-space-2)', borderRadius: 'var(--vn-radius-md)' }}>
              <span>Receiver: {selected.mailer_receiver || '—'}</span>
              <span>CC: {selected.mailer_cc || '—'}</span>
              <span>BCC: {selected.mailer_bcc || '—'}</span>
            </div>
          ) : null}
        </>
      ) : null}

      {rejecting ? (
        <>
          <Field label="Reject reason" required>
            {(p) => <Textarea {...p} value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Why is this step rejected?" autoFocus />}
          </Field>
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)', justifyContent: 'flex-end' }}>
            <Button size="sm" variant="ghost" onClick={() => setRejecting(false)} disabled={pending}>Cancel</Button>
            <Button size="sm" variant="danger" onClick={submitReject} disabled={!reason.trim() || pending}>Confirm reject</Button>
          </div>
        </>
      ) : (
        <div style={{ display: 'flex', gap: 'var(--vn-space-2)', justifyContent: 'flex-end' }}>
          <Button size="sm" variant="danger" onClick={() => setRejecting(true)} disabled={pending}><X size={14} /> Reject</Button>
          <Button size="sm" variant="primary" onClick={approve} disabled={pending}><Check size={14} /> Approve</Button>
        </div>
      )}
    </div>
  );
}
